/**
 * @file Text prompt modal — the app-styled replacement for window.prompt().
 * Same shape as price-modal.js: backdrop, title, one field, Cancel / confirm.
 * On phones the shared `.modal` rules turn it into a bottom sheet, which is
 * what a serial-number entry or a note for Finance needs there (the native
 * prompt is a cramped system box with no context).
 *
 * Resolves with the trimmed value, or null when dismissed (Cancel, Escape,
 * backdrop tap).
 */

import { el } from '../utils.js';

/**
 * @param {object} o
 * @param {string}  o.title         Dialog title.
 * @param {string}  o.label         Field label.
 * @param {string}  [o.value]       Initial value.
 * @param {string}  [o.placeholder] Example value, never a sentence.
 * @param {string}  [o.confirmLabel]
 * @param {string}  [o.cancelLabel]
 * @param {boolean} [o.required]    Refuse an empty submit.
 * @param {boolean} [o.mono]        Serial-style input: monospace, no autocorrect, caps.
 * @param {boolean} [o.multiline]   Textarea instead of a single line.
 * @returns {Promise<string|null>}
 */
export function promptText({
  title, label, value = '', placeholder = '', confirmLabel = 'OK', cancelLabel = 'Cancel',
  required = false, mono = false, multiline = false,
}) {
  return new Promise((resolve) => {
    const backdrop = el('div', { class: 'modal-backdrop open' });
    const input = multiline
      ? el('textarea', { rows: 3, placeholder })
      : el('input', {
          type: 'text', placeholder,
          autocomplete: 'off',
          ...(mono ? { autocapitalize: 'characters', autocorrect: 'off', spellcheck: 'false', style: 'font-family:var(--font-serial)' } : {}),
        });
    input.value = value;

    const close = (result) => {
      document.removeEventListener('keydown', onKey);
      backdrop.remove();
      resolve(result);
    };
    const confirm = () => {
      const v = input.value.trim();
      if (required && !v) { input.setAttribute('aria-invalid', 'true'); input.focus(); return; }
      close(v);
    };
    const onKey = (e) => { if (e.key === 'Escape') close(null); };
    document.addEventListener('keydown', onKey);
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !multiline) { e.preventDefault(); confirm(); }
    });
    backdrop.addEventListener('click', (e) => { if (e.target === backdrop) close(null); });

    backdrop.appendChild(el('div', { class: 'modal', role: 'dialog', 'aria-label': title },
      el('h2', null, title),
      el('div', { class: 'field', style: 'margin-top:16px' }, el('label', null, label), input),
      el('div', { class: 'row', style: 'margin-top:24px;justify-content:flex-end;gap:8px' },
        el('button', { class: 'btn-ghost btn', onClick: () => close(null) }, cancelLabel),
        el('button', { class: 'btn', onClick: confirm }, confirmLabel),
      ),
    ));
    document.body.appendChild(backdrop);
    input.focus();
    if (!multiline && value) input.select();
  });
}
